import { useFeatureFlag } from '@artic/ui/feature-flag';
import { FullHeightChrome, FullHeightFill, FullHeightStack } from '@artic/ui/full-height-layout';
import { Skeleton } from '@artic/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { usePartnerId } from '../../features/inbox/use-partner-name';
import { ProjectsListViewToggle } from '../../features/project-planning/projects-list-view-toggle';
import { projectPlanningListQueryOptions } from '../../features/project-planning/queries';
import { ProjectTimeline } from '../../features/project-timeline/project-timeline';
import {
  ProjectTimelineFilterBar,
  ProjectTimelineViewBy,
} from '../../features/project-timeline/project-timeline-filter-bar';

export const Route = createFileRoute('/projects/timeline')({
  staticData: { breadcrumb: 'Timeline', layout: 'full-height' },
  component: ProjectsTimelinePage,
});

function ProjectsTimelinePage() {
  const planner = useFeatureFlag('planner');

  if (!planner) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Feature Not Available</h2>
        <p className="text-muted-foreground text-sm">
          Production Planning is not enabled for your organisation.
        </p>
      </div>
    );
  }

  return (
    <FullHeightStack>
      <ProjectsTimelineContent />
    </FullHeightStack>
  );
}

function ProjectsTimelineContent() {
  const partnerId = usePartnerId();
  const navigate = useNavigate();

  const {
    data: projects,
    isLoading,
    isError,
  } = useQuery(projectPlanningListQueryOptions(partnerId));

  const allProjects = projects ?? [];

  return (
    <>
      {/* Single dense toolbar row: title + view toggle + filters + view-by. */}
      <FullHeightChrome className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-3 pt-2.5 pb-1.5">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">Projects</h1>
          <ProjectsListViewToggle activeView="timeline" />
        </div>
        {!isLoading && !isError && (
          <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <ProjectTimelineFilterBar projects={allProjects} />
            <ProjectTimelineViewBy />
          </div>
        )}
      </FullHeightChrome>
      <FullHeightFill className="px-3 pb-3">
        {isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : isError ? (
          <div className="flex h-full flex-col items-center justify-center gap-2">
            <h2 className="text-lg font-semibold">Error Loading Projects</h2>
            <p className="text-muted-foreground text-sm">
              Could not load the project list. Please try again.
            </p>
          </div>
        ) : (
          <ProjectTimeline
            projects={allProjects}
            onSelectProject={(projectId) =>
              void navigate({ to: '/projects/$projectId', params: { projectId } })
            }
          />
        )}
      </FullHeightFill>
    </>
  );
}
